// src/components/Landing.tsx
//
// Section-based hybrid landing page (see docs/LANDING_PAGE_WEBGL_PLAN.md).
// Normal document scroll with real DOM sections; the hero hosts the career
// pipeline (WebGL on capable devices via HeroPipelineHost — R2 — with the
// CSS HeroPipeline as fallback). GSAP drives the scroll-triggered card journey
// (R3). Content is honest — no fabricated stats or testimonials.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import HeroRadar from "./HeroRadar";
import StoryJourney from "./StoryJourney";
import KanbanFlow from "./KanbanFlow";
import TopDownBoard from "./TopDownBoard";
import "../styles/Landing.css";

// ---------------------------------------------------------------------------
// Content (all truthful to the actual product)
// ---------------------------------------------------------------------------

const METRICS: { value: number; suffix?: string; label: string }[] = [
  { value: 5, label: "pipeline stages, tracked" },
  { value: 5, label: "job boards, one search" },
  { value: 3, label: "AI match modes" },
  { value: 100, suffix: "%", label: "scoped to your region" },
];

// Interactive feature switcher (restored from the first landing — exact copy).
const FEATURES: {
  icon: string;
  title: string;
  tagline: string;
  body: string;
  accent: string;
}[] = [
  {
    icon: "🎯",
    title: "AI job matching",
    tagline: "The right roles, ranked first",
    body: "We scan multiple job boards across your country and province, then score every role against your resume, experience, and education — three ways. You see the best fits first, not a firehose.",
    accent: "#6366f1",
  },
  {
    icon: "✍️",
    title: "Resume & cover letters",
    tagline: "Tailored in seconds",
    body: "Build a master resume and cover letter once, then let AI rewrite them for each job description — matching keywords, tone, and requirements while you keep full control of every edit.",
    accent: "#22d3ee",
  },
  {
    icon: "📋",
    title: "Application tracking",
    tagline: "Never lose the thread",
    body: "Every application in one board: status, dates, the resume you used, and notes. Filter, search, and always know exactly where you stand across dozens of roles.",
    accent: "#38bdf8",
  },
  {
    icon: "📬",
    title: "Email that tracks itself",
    tagline: "Replies, logged automatically",
    body: "Connect your inbox and Sumbitrbuddy detects employer replies — accepts, rejections, interview invites — and files them against the right application on its own.",
    accent: "#a855f7",
  },
  {
    icon: "🎤",
    title: "Interview prep",
    tagline: "Walk in ready",
    body: "Practice with role-specific questions and mock interviews, and get curated prep resources the moment an application moves to the interview stage.",
    accent: "#ec4899",
  },
  {
    icon: "🔔",
    title: "Smart alerts",
    tagline: "Nudges at the right moment",
    body: "Interview reminders, follow-up nudges when you've gone quiet, new-job alerts for your region, and warnings before a posting closes for good.",
    accent: "#34d399",
  },
];

const STEPS = [
  {
    n: "1",
    title: "Build your profile",
    body: "Add your resume, experience, and the roles and locations you're targeting. A few minutes, once.",
  },
  {
    n: "2",
    title: "Get matched & tailored",
    body: "Sumbitrbuddy surfaces roles that fit — scored and region-scoped — and tailors your resume to each.",
  },
  {
    n: "3",
    title: "Track all the way to the offer",
    body: "Apply, track every stage, prep for interviews, compare offers, and get nudged at the right moments.",
  },
];

// ---------------------------------------------------------------------------
// Count up when scrolled into view
// ---------------------------------------------------------------------------
const CountUp: React.FC<{ value: number; suffix?: string }> = ({ value, suffix }) => {
  const [n, setN] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        const start = performance.now();
        const dur = 1100;
        const tick = (t: number) => {
          const p = Math.min(1, (t - start) / dur);
          const eased = 1 - Math.pow(1 - p, 3);
          setN(Math.round(eased * value));
          if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value]);
  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
};

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [activeFeature, setActiveFeature] = useState(0);
  const [featurePaused, setFeaturePaused] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate("/dashboard", { replace: true });
  }, [isAuthenticated, navigate]);

  // Auto-advance the feature switcher (pauses on hover/interaction).
  useEffect(() => {
    if (featurePaused) return;
    const id = window.setInterval(() => {
      setActiveFeature((i) => (i + 1) % FEATURES.length);
    }, 3800);
    return () => window.clearInterval(id);
  }, [featurePaused]);

  // Scroll-reveal for sections/cards.
  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("reveal-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const scrollTo = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="lp">
      {/* Nav */}
      <header className="lp-nav">
        <div className="lp-wrap lp-nav-inner">
          <Link to="/" className="lp-brand" aria-label="Sumbitrbuddy home">
            <span className="lp-brand-mark">S</span>
            <span className="lp-brand-word">Sumbitrbuddy</span>
          </Link>
          <nav className="lp-nav-actions">
            <Link to="/login" className="lp-btn lp-btn-ghost">Sign in</Link>
            <Link to="/signup" className="lp-btn lp-btn-primary">Get started</Link>
          </nav>
        </div>
      </header>

      {/* Section 1 — Hero (two-column: bold headline left, 3D pipeline right) */}
      <section className="lp-hero">
        <div className="lp-hero-bg" aria-hidden="true" />
        <div className="lp-wrap lp-hero-grid">
          <div className="lp-hero-copy">
            <span className="lp-hero-pill">
              <span className="lp-hero-pill-dot" />
              AI scanning every board in your region — live
            </span>
            <h1 className="lp-hero-title">
              Every stage of your <span className="lp-hero-accent">career journey</span>
            </h1>
            <p className="lp-hero-sub">
              Sumbitrbuddy tracks every application, tailors every resume, preps
              you for every interview, and helps you compare every offer — the
              whole pipeline, in one place.
            </p>
            <div className="lp-hero-cta">
              <Link to="/signup" className="lp-btn lp-btn-primary lp-btn-lg">
                Get started free
              </Link>
              <a href="#features" onClick={scrollTo("features")} className="lp-btn lp-btn-outline lp-btn-lg">
                Explore features
              </a>
            </div>
          </div>
          <div className="lp-hero-visual">
            <HeroRadar />
          </div>
        </div>
      </section>

      {/* Section 2 — Metrics (honest) */}
      <section className="lp-metrics">
        <div className="lp-wrap">
          <h2 className="lp-metrics-title reveal">
            We track applications. You own the offers.
          </h2>
          <div className="lp-metrics-grid">
            {METRICS.map((m) => (
              <div className="lp-metric reveal" key={m.label}>
                <div className="lp-metric-num">
                  <CountUp value={m.value} suffix={m.suffix} />
                </div>
                <div className="lp-metric-label">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3 — Vertical scroll-story: the emotional spine */}
      <StoryJourney />

      {/* Section 3b — Kanban in motion (colored columns pan on scroll) */}
      <KanbanFlow />

      {/* Section 3c — Top-down organization (headline + rich board) */}
      <TopDownBoard />

      {/* Section 4 — Interactive feature switcher (restored) */}
      <section className="lp-section lp-section-alt" id="features">
        <div className="lp-wrap">
          <div className="lp-section-head reveal">
            <h2 className="lp-section-title">Everything your job hunt needs</h2>
            <p className="lp-section-sub">
              One companion for the whole journey — click through what it does.
            </p>
          </div>
          <div
            className="lp-switch reveal"
            onMouseEnter={() => setFeaturePaused(true)}
            onMouseLeave={() => setFeaturePaused(false)}
          >
            <div className="lp-switch-list">
              {FEATURES.map((f, i) => (
                <button
                  key={f.title}
                  className={`lp-switch-item ${i === activeFeature ? "is-active" : ""}`}
                  onClick={() => setActiveFeature(i)}
                  style={
                    i === activeFeature
                      ? ({ ["--accent" as string]: f.accent } as React.CSSProperties)
                      : undefined
                  }
                >
                  <span className="lp-switch-icon">{f.icon}</span>
                  <span className="lp-switch-name">{f.title}</span>
                </button>
              ))}
            </div>
            <div
              className="lp-switch-panel"
              key={activeFeature}
              style={{ ["--accent" as string]: FEATURES[activeFeature].accent } as React.CSSProperties}
            >
              <div className="lp-switch-badge">{FEATURES[activeFeature].icon}</div>
              <div className="lp-switch-tagline">{FEATURES[activeFeature].tagline}</div>
              <h3 className="lp-switch-title">{FEATURES[activeFeature].title}</h3>
              <p className="lp-switch-body">{FEATURES[activeFeature].body}</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="lp-section">
        <div className="lp-wrap">
          <div className="lp-section-head reveal">
            <h2 className="lp-section-title">Three steps from lost to landed</h2>
          </div>
          <div className="lp-steps">
            <div className="lp-steps-line" aria-hidden="true" />
            {STEPS.map((s, i) => (
              <div className="lp-step reveal" key={s.n} style={{ transitionDelay: `${i * 120}ms` }}>
                <div className="lp-step-n">{s.n}</div>
                <h3 className="lp-step-title">{s.title}</h3>
                <p className="lp-step-body">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5 — Be among the first (honest early-adopter framing) */}
      <section className="lp-first">
        <div className="lp-first-glow" aria-hidden="true" />
        <div className="lp-wrap lp-first-inner reveal">
          <span className="lp-eyebrow lp-eyebrow-dark">Just launched</span>
          <h2 className="lp-first-title">Be among the first to land with Sumbitrbuddy.</h2>
          <p className="lp-first-sub">
            Sumbitrbuddy is brand new — and you can shape it. Join early, use every
            feature free while we grow, and turn a scattered, stressful hunt into a
            tracked, winnable process from day one.
          </p>
          <div className="lp-first-chips">
            <span>✓ Free to start</span>
            <span>✓ No credit card</span>
            <span>✓ Your data encrypted</span>
            <span>✓ Built in Canada</span>
          </div>
          <Link to="/signup" className="lp-btn lp-btn-primary lp-btn-lg">
            Claim your early spot
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="lp-cta">
        <div className="lp-cta-glow" aria-hidden="true" />
        <div className="lp-wrap lp-cta-inner reveal">
          <h2 className="lp-cta-title">Ready to own your next offer?</h2>
          <p className="lp-cta-sub">Take control of your entire job search today.</p>
          <Link to="/signup" className="lp-btn lp-btn-primary lp-btn-lg">
            Create your free account
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="lp-footer">
        <div className="lp-wrap lp-footer-inner">
          <div className="lp-brand">
            <span className="lp-brand-mark">S</span>
            <span className="lp-brand-word">Sumbitrbuddy</span>
          </div>
          <p className="lp-footer-tag">Your job search, supported.</p>
          <div className="lp-footer-links">
            <Link to="/login">Sign in</Link>
            <Link to="/signup">Get started</Link>
          </div>
        </div>
        <div className="lp-footer-copy">© {new Date().getFullYear()} Sumbitrbuddy. All rights reserved.</div>
      </footer>
    </div>
  );
};

export default Landing;
